import {
  customPriceBreakdownTitle,
  knownPriceBreakdownTitle,
} from 'checkout/constants/priceBreakdownTitles'
import { isFlightItem } from 'checkout/lib/utils/flights/cart'
import { getCheckoutFlightV2SearchUrl, getStandaloneFlightsV2SearchUrl } from 'checkout/lib/utils/flights/links'
import { getFlightDateChangeSearchKey } from 'checkout/lib/utils/flights/dateChangeUtils'
import { arrayToMap, arrayToObject, nonNullable, sum } from 'lib/array/arrayUtils'
import {
  buildDateChangeFlightItemBreakdownView,
  buildFlightItemBreakdownView,
  formatFareType,
  formatFlightPassengersShorthand, formatJourneyShorthand, formatJourneySummaryTitle,
  formatTravellerCount,
} from 'lib/checkout/flights/format'
import { createSelector } from 'reselect'

import { getDateChangeJourneyV2IdKey, getFlightLoyaltyProductType, getFlightLoyaltyProductTypeRank, getJourneyV2IdKey, hasCheckoutFlightItemsWithEmbeddedInsurance } from 'lib/flights/flightUtils'
import { pruneLegacyBaggageSelections } from 'lib/flights/pruneLegacyBaggageSelections'
import { aggregateFlightServiceRows, type FlightServiceRowData } from 'checkout/Components/Shared/Summary/BreakdownItems/flightServiceRows'
import { convertJourneyV2ToJourneyFlight } from 'api/lib/convertJourneyV2'
import { FlightsFareTypes, FlightViewTypes } from 'constants/flight'
import { findPostPurchaseCheckout, isBookingProtectionItem, isInsuranceItem, isLEHotelItem, isLuxPlusSubscriptionItem, isSubscriptionJoinItem } from 'lib/checkout/checkoutUtils'
import { reselectShallowEqual } from 'lib/selector/selectorUtils'
import { getAllOrderItems, isBookingProtectionOrderItem, isFlightOrderItem, isInsuranceOrderItem, isLuxPlusSubscriptionOrderItem } from 'lib/order/orderUtils'
import { getAgentHubCommissionItemKey } from 'lib/agentHub/agentHubUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import {
  computeDepartingFeederDelta,
  getBudgetedFeederLegFare,
  resolveDepartingFeederReference,
} from 'lib/flights/feederBundleLabels'
import { getCheckoutLoyaltyClassicRewardsStatus, getCheckoutLoyaltyClassicRewards } from 'checkout/lib/utils/classicRewards/classicRewardCheckoutState'
import { getCanRedeemLuxPlusBenefitsOnOrder, getIsEnabledFlightsBundledLuxPlusPricing, getIsEnabledFlightsLuxPlusPricing } from 'luxPlus/selectors/featureToggle'
import { FlightChangeDatesPageStep } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/flights'

const selectFlightsSeatSelectionsByKey = (state: App.State) => state.flights.seatSelections ?? {}
const selectCheckoutSeatSelectionsByKey = (state: App.State) => state.checkout.form.flightSeatSelections ?? {}

export const getFlightSeatSelectionsByKey = createSelector(
  [selectCheckoutSeatSelectionsByKey, selectFlightsSeatSelectionsByKey],
  (checkoutSeatSelections, flightSeatSelections): Record<string, Record<string, Record<string, App.FlightsSeatsItem>>> => ({
    ...checkoutSeatSelections,
    ...flightSeatSelections,
  }),
)

const selectFlightsBaggageSelectionsByKey = (state: App.State) => state.flights.baggageSelections ?? {}
const selectCheckoutBaggageSelectionsByKey = (state: App.State) => state.checkout.form.flightBaggageSelections ?? {}

/**
 * The single read of the two baggage-selection slices, and the only place selections enter the
 * checkout. Pruning legacy flight-keyed records here — rather than at each consumer — is what
 * guarantees the modal and the price summary can never disagree about which bags are selected: they
 * both read this, so neither can see a record the other has discarded.
 */
export const getFlightBaggageSelectionsByKey = createSelector(
  [selectCheckoutBaggageSelectionsByKey, selectFlightsBaggageSelectionsByKey],
  (checkoutBaggageSelections, flightBaggageSelections): Record<string, Record<string, Record<string, App.FlightBaggageSelection>>> => pruneLegacyBaggageSelections({
    ...checkoutBaggageSelections,
    ...flightBaggageSelections,
  }),
)

const createTravellerElements = (travellers: Array<{ type: string }> = [], penaltyBreakdown: Record<string, App.FlightChangePenaltyBreakdown> = {}) => {
  const intl = getCurrentIntl()
  const travellerCounts = {
    adult: travellers.filter(t => t.type === 'adult').length,
    child: travellers.filter(t => t.type === 'child').length,
    infant: travellers.filter(t => t.type === 'infant').length,
  }

  const priceMap = {
    adult: penaltyBreakdown.adult?.amount || 0,
    child: penaltyBreakdown.child?.amount || 0,
    infant: penaltyBreakdown.infant?.amount || 0,
  }

  return Object.entries(travellerCounts)
    .filter(([_, count]) => count > 0)
    .map(([type, count]) => ({
      id: type,
      left: {
        type: 'text' as const,
        value: formatTravellerCount(intl, type, count),
      },
      right: {
        type: 'price' as const,
        value: priceMap[type as keyof typeof priceMap] * count,
      },
    }))
}

function getSelectedDateChangeJourneys(
  flightItems: Array<App.Checkout.FlightItem>,
  journeysById: Record<string, App.AnyJourney>,
): { hasSelectedJourneys: boolean; hasRequiredData: boolean; journeys: Array<App.JourneyV2> } {
  const journeyKeys = flightItems
    .filter(item => item.isFlightDateChange)
    .flatMap(item => item.flights.map(flight => getDateChangeJourneyV2IdKey(item.reservationId!, item.searchId!, flight.journeyId)))
  const journeys = journeyKeys.map(key => journeysById[key] as App.JourneyV2 | undefined)

  return {
    hasSelectedJourneys: journeyKeys.length > 0,
    hasRequiredData: journeyKeys.length > 0 && journeys.every(Boolean),
    journeys: journeys.filter((journey): journey is App.JourneyV2 => !!journey),
  }
}

function getFirstAvailableJourneyPerLeg(fares: Array<Array<App.JourneyV2>> = []): Array<App.JourneyV2> {
  return fares
    .map(legFares => legFares[0])
    .filter((journey): journey is App.JourneyV2 => !!journey)
}

const passengerPriceKeys = ['adult', 'child', 'infant'] as const

function isBeforeFlightDateChangeNewFlightSelection(currentStep?: string): boolean {
  return currentStep === FlightChangeDatesPageStep.SELECT_FLIGHTS_TO_CHANGE ||
    currentStep === FlightChangeDatesPageStep.SELECT_DATES_TO_CHANGE
}

export const getIsBeforeFlightDateChangeNewFlightSelection = createSelector(
  (state: App.State) => state.flights.flightsDateChange?.currentStep,
  isBeforeFlightDateChangeNewFlightSelection,
)

export const getShouldHideEstimatedAirlineChangeFee = getIsBeforeFlightDateChangeNewFlightSelection

const getCurrentFlightDateChangeSearchState = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder?.flightItems[0]?.reservationId,
  (state: App.State) => state.flights.flightsDateChange?.selectedLegWithNewDepartureDate,
  (state: App.State) => state.flights.flightsDateChangeSearch,
  (reservationId, selectedLegsWithDates, flightsDateChangeSearch) => {
    if (!reservationId || !selectedLegsWithDates?.length) {
      return undefined
    }

    const searchKey = getFlightDateChangeSearchKey(reservationId, selectedLegsWithDates)
    return flightsDateChangeSearch?.[searchKey]
  },
)

export const getFlightDateChangeSearchAirlineFeeJourneys = createSelector(
  (state: App.State) => getCurrentFlightDateChangeSearchState(state),
  currentSearchState => currentSearchState?.isFetching ? [] : getFirstAvailableJourneyPerLeg(currentSearchState?.fares),
)

export const getIsFlightDateChangeSearchLoading = createSelector(
  (state: App.State) => state.flights.flightsDateChange?.currentStep,
  (state: App.State) => state.checkout.cart.existingOrder?.flightItems[0]?.reservationId,
  (state: App.State) => state.flights.flightsDateChange?.selectedLegWithNewDepartureDate,
  (state: App.State) => getCurrentFlightDateChangeSearchState(state),
  (currentStep, reservationId, selectedLegsWithDates, currentSearchState): boolean => {
    if (
      currentStep !== FlightChangeDatesPageStep.SELECT_NEW_FLIGHTS ||
      !reservationId ||
      !selectedLegsWithDates?.length
    ) {
      return false
    }

    return !currentSearchState || currentSearchState.isFetching
  },
)

export const getIsCurrentFlightDateChangeSelectionValid = createSelector(
  (state: App.State) => state.flights.flightsDateChange?.currentStep,
  (state: App.State) => getFlightItems(state),
  (state: App.State) => getCurrentFlightDateChangeSearchState(state),
  (currentStep, flightItems, currentSearchState): boolean => {
    const dateChangeFlightItems = flightItems.filter(item => item.isFlightDateChange)

    if (dateChangeFlightItems.length === 0 || isBeforeFlightDateChangeNewFlightSelection(currentStep)) {
      return false
    }

    if (currentStep === FlightChangeDatesPageStep.SELECT_NEW_FLIGHTS) {
      return !!currentSearchState?.searchId &&
        !currentSearchState.isFetching &&
        dateChangeFlightItems.every(item => item.searchId === currentSearchState.searchId)
    }

    return true
  },
)

export const getIsFlightDateChangeAirlineChangeFeeLoading = createSelector(
  (state: App.State) => getIsFlightDateChangeSearchLoading(state),
  (state: App.State) => getIsCurrentFlightDateChangeSelectionValid(state),
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.flights.journeysById,
  (isFlightDateChangeSearchLoading, isCurrentFlightDateChangeSelectionValid, flightItems, journeysById): boolean => {
    if (isFlightDateChangeSearchLoading) {
      return true
    }

    const selectedDateChangeJourneyState = isCurrentFlightDateChangeSelectionValid ?
        getSelectedDateChangeJourneys(flightItems, journeysById) :
        { hasSelectedJourneys: false, hasRequiredData: false, journeys: [] }

    return selectedDateChangeJourneyState.hasSelectedJourneys && !selectedDateChangeJourneyState.hasRequiredData
  },
)

function createAirlineChangeFeeTravellerElements(journeys: Array<App.JourneyV2>) {
  const intl = getCurrentIntl()

  return passengerPriceKeys
    .map(type => {
      const firstPrice = journeys.find(journey => journey.price?.[type])?.price?.[type]
      const count = firstPrice?.passengerTypeQuantity.quantity ?? 0
      const price = sum(journeys, journey => journey.price?.[type]?.airlineChangeFee ?? 0)

      return { type, count, price }
    })
    .filter(({ count }) => count > 0)
    .map(({ type, count, price }) => ({
      id: type,
      left: {
        type: 'text' as const,
        value: formatTravellerCount(intl, type, count),
      },
      right: {
        type: 'price' as const,
        value: price * count,
      },
    }))
}

function getPendingReturningLegPrice(item: App.Checkout.FlightItem): number {
  return item.bundleBudget?.returningLegBudget ?? (item.quotedFare ?? 0) / 2
}

function getReturnFarePendingPrice(item: App.Checkout.FlightItem, totalRoundTripPrice: number): number {
  return item.bundleBudget
    ? getBudgetedFeederLegFare(totalRoundTripPrice, item.bundleBudget.tripBudget, item.bundleBudget.tripBuffer)
    : totalRoundTripPrice
}

function getBudgetedSelectedDepartingLegPrice(item: App.Checkout.FlightItem, departingFare: number): number {
  if (!item.bundleBudget) return departingFare
  const { departingLegBudget, departingLegBuffer } = item.bundleBudget
  if (departingLegBudget && departingLegBudget > 0) {
    return getBudgetedFeederLegFare(departingFare, departingLegBudget, departingLegBuffer)
  }
  // FLY-5300: departing leg has no own budget — the trip allowance is fully on the returning
  // leg (`getPendingReturningLegPrice` already returns the full returning budget). Contribute
  // only the displayed tile delta here (0 within fallback band, excess above) so the summary
  // change matches the `+A$X` shown on the departing tile/modal.
  const { baseBudget, bufferBudget } = resolveDepartingFeederReference({ bundleBudget: item.bundleBudget })
  return computeDepartingFeederDelta({ optionFare: departingFare, baseBudget, bufferBudget })
}

function getFlightBreakdownLuxPlusRegularPrice(items: Array<App.Checkout.FlightItemBreakdownView>): number | undefined {
  const hasLuxPlusRegularPrice = items.some(item =>
    item.journey.isLuxPlusFare && !!item.journey.regularPrice?.all.totalFare,
  )
  if (!hasLuxPlusRegularPrice) {
    return undefined
  }

  return sum(items, item => {
    if (item.journey.isLuxPlusFare && item.journey.regularPrice?.all.totalFare) {
      return item.journey.regularPrice.all.totalFare
    }

    return item.price ?? item.journey.cost ?? 0
  })
}

export function getFlightItemViewLuxPlusRegularPrice(itemView: App.Checkout.FlightItemView): number | undefined {
  // Before the return leg is selected, checkout totals use a pending whole-trip member price.
  // Compare it with the regular whole-trip price instead of a one-leg regular fare.
  const isOneWay = itemView.viewType === FlightViewTypes.ONE_WAY ||
    itemView.fareType === FlightsFareTypes.ONE_WAY
  const usePendingReturnPrice = itemView.flights.length === 1 && !isOneWay
  const hasLuxPlusRegularPrice = itemView.flights.some(flight => {
    const isSelectedLuxPlusFare = !!flight.fareFamily?.bookingInfo?.isLuxPlusFare || flight.journeyFlight.isLuxPlusFare

    return isSelectedLuxPlusFare && (
      flight.fareFamily?.regularCost?.all !== undefined ||
      flight.journeyFlight.regularPrice?.all.totalFare !== undefined ||
      (usePendingReturnPrice && flight.journeyFlight.regularPrice?.all.totalRoundTripPrice !== undefined)
    )
  })
  if (!hasLuxPlusRegularPrice) {
    return undefined
  }

  const regularPrice = sum(itemView.flights, flight => {
    const isSelectedLuxPlusFare = !!flight.fareFamily?.bookingInfo?.isLuxPlusFare || flight.journeyFlight.isLuxPlusFare
    const memberPrice = flight.fareFamily?.cost?.all ?? flight.cost ?? flight.journeyFlight.cost ?? 0

    if (!isSelectedLuxPlusFare) {
      return memberPrice
    }
    if (
      usePendingReturnPrice &&
      flight.journeyFlight.regularPrice?.all.totalRoundTripPrice !== undefined
    ) {
      return flight.journeyFlight.regularPrice.all.totalRoundTripPrice
    }
    if (flight.fareFamily?.regularCost?.all !== undefined) {
      return flight.fareFamily.regularCost.all
    }
    if (flight.journeyFlight.regularPrice?.all.totalFare !== undefined) {
      return flight.journeyFlight.regularPrice.all.totalFare
    }

    return memberPrice
  })
  return regularPrice
}

function hasBundledItemOverlap(
  nonCharterItem: App.Checkout.FlightItem,
  charterItem: App.Checkout.FlightItem,
): boolean {
  return !!nonCharterItem.bundledItemIds?.some(itemId => charterItem.bundledItemIds?.includes(itemId))
}

function findActiveFeederItem(
  nonCharterItems: Array<App.Checkout.FlightItem>,
  charterItems: Array<App.Checkout.FlightItem>,
): App.Checkout.FlightItem | undefined {
  const charterItem = charterItems[0]
  if (!charterItem) return undefined

  return nonCharterItems.find(item => {
    const isNonMelConnector = item.originAirportCode !== charterItem.originAirportCode
    const connectsToCharterOrigin = item.destinationAirportCode === charterItem.originAirportCode
    return isNonMelConnector && connectsToCharterOrigin && hasBundledItemOverlap(item, charterItem)
  })
}

export const getFlightItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.FlightItem> => items.filter(isFlightItem),
  reselectShallowEqual,
)

export const getCharterFlightItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.FlightItem> => items
    .filter(item => isFlightItem(item))
    .filter(item => item.charterBundleRoute),
  reselectShallowEqual,
)

export const getNonCharterFlightItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.FlightItem> => items
    .filter(item => isFlightItem(item))
    .filter(item => !item.charterBundleRoute),
  reselectShallowEqual,
)

export const getIsCharterFlightCheckout = (state: App.State) => getCharterFlightItems(state).length > 0

function hasCompleteSelectedJourneys(item: App.Checkout.FlightItem): boolean {
  return item.flights.length > 0 && item.flights.every(flight => !!flight.journeyId)
}

function isCharterBundleOffer(offer: App.AnyOffer | undefined): boolean {
  return isFlashHotelOffer(offer) &&
    offer.offerFlightsEnabled &&
    offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
}

function isFlightBundledWithAccommodation(flightItem: App.Checkout.FlightItem, accommodationItemId: string): boolean {
  return !flightItem.bundledItemIds?.length || flightItem.bundledItemIds.includes(accommodationItemId)
}

export const getHasInvalidCharterBundleFlightSelection = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.offers,
  (items, offers): boolean => {
    const charterAccommodationItems = items
      .filter(isLEHotelItem)
      .filter(item => isCharterBundleOffer(offers[item.offerId]))

    if (!charterAccommodationItems.length) return false

    const validCharterFlightItems = items
      .filter(isFlightItem)
      .filter(item => !!item.charterBundleRoute)
      .filter(hasCompleteSelectedJourneys)

    return charterAccommodationItems.some(accommodationItem =>
      !validCharterFlightItems.some(flightItem =>
        isFlightBundledWithAccommodation(flightItem, accommodationItem.itemId),
      ),
    )
  },
)

/**
 * Returns the feeder bundle budget propagated from the offer page.
 *
 * `bundleBudget` lives on the FEEDER flight item (non-charter) for non-MEL Maldives bundle
 * flows. `isFeederBundle` is true iff such a budget exists; this is the single boolean used
 * to gate `+$X` budget labels and to forward `isFeederBundle: true` to /flightsearch.
 */
export const getFeederBundleBudget = createSelector(
  (state: App.State) => getNonCharterFlightItems(state),
  (state: App.State) => getCharterFlightItems(state),
  (nonCharterItems, charterItems): { isFeederBundle: boolean, bundleBudget?: App.Checkout.FeederBundleBudget } => {
    // Feeder bundle requires both legs of the charter (Maldives bundle) plus a non-charter feeder leg.
    if (charterItems.length === 0) return { isFeederBundle: false }
    const feederItem = nonCharterItems.find(item => !!item.bundleBudget)
    const bundleBudget = feederItem?.bundleBudget
    return {
      isFeederBundle: !!bundleBudget,
      bundleBudget,
    }
  },
)

/**
 * Returns the feeder origin airport code (e.g. 'SYD') when the cart currently holds an
 * active feeder leg alongside the Maldives charter bundle.
 *
 * After `skipFlights()` removes the non-charter feeder item, this selector returns
 * `undefined`, so callers (e.g. /select, GET fare) signal to svc-flights that the
 * charter fares should pick up the MEL self-feeder markup dynamically.
 *
 * This does not depend on `bundleBudget`, because one-zero-leg feeder budgets can be
 * absent in older persisted carts while the active feeder route still exists.
 */
export const getFeederOriginAirportCode = createSelector(
  (state: App.State) => getNonCharterFlightItems(state),
  (state: App.State) => getCharterFlightItems(state),
  (nonCharterItems, charterItems): string | undefined => {
    const feederItem = findActiveFeederItem(nonCharterItems, charterItems)
    return feederItem?.originAirportCode
  },
)

export const getCharterFlightDepartureDate = createSelector(
  (state: App.State) => getCharterFlightItems(state),
  (charterFlightItems): string | undefined => charterFlightItems[0]?.flights.at(0)?.departingDate,
)

export const getCharterFlightReturnArrivalDate = createSelector(
  (state: App.State) => getCharterFlightItems(state),
  (charterFlightItems): string | undefined => {
    const arrivalDateTime = charterFlightItems[0]?.flights.at(-1)?.arrivalDateTime
    return arrivalDateTime?.split('T')[0]
  },
)

export const flightSearchInProgress = createSelector(
  (state: App.State) => state.flights.searchFlights,
  (state: App.State) => state.flights.searchV2Flights,
  (flightsV1, flightsV2): boolean => {
    const journeysV1Fetching = Object.values(flightsV1).flatMap(flight => flight?.fetching).filter(Boolean)
    const journeysV2Fetching = Object.values(flightsV2).flatMap(flight => flight?.fetching).filter(Boolean)
    return !!(journeysV1Fetching.length || journeysV2Fetching.length)
  },
)

export const getBundledFlightItems = createSelector(
  (state: App.State) => getFlightItems(state),
  (items): Array<App.Checkout.FlightItem> => items.filter(item => !!item.bundledItemIds?.length),
  reselectShallowEqual,

)

export const cartIncludesFlights = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): boolean => items.some(isFlightItem),
)

function mapFlightV2ToView(
  item: App.Checkout.FlightItem,
  airports: Array<App.Airport>,
  journeys: Record<string, App.JourneyV2> = {},
): App.Checkout.FlightItemView {
  const intl = getCurrentIntl()
  const fares = sum(item.flights.map(flight =>
    flight.fareFamily?.cost?.all ?? flight.cost ?? 0,
  ))
  const surcharge = sum(item.flights.map(flight => journeys[flight.journeyId]?.price.all.totalFees ?? 0))

  const isBundled = (item.bundledItemIds?.length ?? 0) > 0

  const journeysFareTypes = item.flights.map(flight => journeys[flight.journeyId]?.fareType)

  const totalFare = item.totalFare ?? 0
  const isReturnFares = journeysFareTypes.includes(FlightViewTypes.RETURN)
  let price = totalFare > 0 ? totalFare : fares
  if (price === 0 && item.quotedFare && item.flights.length === 0) {
    price = item.quotedFare
  }

  const departing = item.flights[0] ? journeys[item.flights[0].journeyId] : undefined
  const returning = item.flights[1] ? journeys[item.flights[1].journeyId] : undefined

  const isNDC = Object.values(journeys).some(journey => journey?.bookingInfo?.offerItemId && journey?.bookingInfo?.offerId)

  if (isReturnFares) {
    // For return fares, if departing and returning is defined, we must use the sum of departingFare and returning fare
    // This happens because after updating the fare with an upsell fare family, each segment total fare is updated individually
    // If the only departing is defined, we can use the totalRoundTripPrice since it represents the price considering for
    // the selected departing flight and the cheapest returning flight paired within it
    price = returning ? fares : getReturnFarePendingPrice(item, departing?.price.all.totalRoundTripPrice ?? 0)
  } else if (departing && !returning && item.viewType !== FlightViewTypes.ONE_WAY) {
    // For V2 two one-ways, if the returning fare is not defined, we must use the departing.price.all.totalFare because
    // it has the updated departing flight price after selecting the fare family. The item.departing.cost is only updated
    // after the select endpoint call. We must also add the item.quotedFare price because the cheapest flight price
    // is already included in the total Hotel + flights package. In order to have correct increase in price based on the relative price
    // displayed in the flight tile, we must add the pending return leg estimate to the total price
    price = getBudgetedSelectedDepartingLegPrice(item, departing.price.all.totalFare) + getPendingReturningLegPrice(item)
  }

  const flightsWithJourneys = item.flights.filter(flight => journeys[flight.journeyId])

  const airportsByKey = arrayToMap(airports, airport => airport.code)

  const usedAirports: Array<{ departing?: App.Airport, arrival?: App.Airport }> = flightsWithJourneys.map(journey => {
    const departing = airportsByKey.get(journey.departingAirportCode)
    const arrival = airportsByKey.get(journey.arrivalAirportCode)

    return {
      departing,
      arrival,
    }
  })

  const firstDepartingCountryCode = usedAirports[0]?.departing?.countryCode

  const isDomestic = usedAirports.every(airport => {
    return airport.arrival?.countryCode === airport.departing?.countryCode && airport.departing?.countryCode === firstDepartingCountryCode
  })

  const loyaltyProductTypes = flightsWithJourneys.map(journey => {
    return getFlightLoyaltyProductType(journey.fareFamily?.cabin?.toLowerCase() || item.selectedCabinClass || '', isDomestic)
  })

  const rankedProductTypes = loyaltyProductTypes.sort((a, b) => {
    return getFlightLoyaltyProductTypeRank(a) - getFlightLoyaltyProductTypeRank(b)
  })

  const flights = flightsWithJourneys.map(flight => ({
    journeyFlight: convertJourneyV2ToJourneyFlight(journeys[flight.journeyId]),
    extras: flight.extras,
    cost: flight.cost,
    fareFamily: flight.fareFamily,
    withEmbeddedInsurance: flight.withEmbeddedInsurance,
  }))

  const result: App.Checkout.FlightItemView = {
    title: `${flights.map(flight => formatJourneyShorthand(flight.journeyFlight, intl)).join(' - ')} - ${formatFareType(intl, item.fareType)}`,
    loyalty: {
      productType: rankedProductTypes[0] || 'dom_economy',
    },
    item,
    journeyId: item.searchId,
    provider: departing?.provider,
    passengers: item.passengers,
    flights,
    designation: intl.formatMessage({
      id: 'PWHuXf7EtRkQF3ABs21k',
      defaultMessage: 'Flights',
      description: 'Flight item designation in checkout breakdown in the checkout flow',
    }),
    validatingCarrier: departing?.carrier,
    validatingCarrierName: departing?.carrierName ?? '',
    validatingCarrierLogo: departing?.carrierLogo ?? '',
    returningCarrierName: returning?.carrierName ?? '',
    returningCarrierLogo: returning?.carrierLogo ?? '',
    fareType: item.fareType,
    totals: {
      price,
      memberPrice: 0, // No data on this
      value: price,
      surcharge,
      memberValue: 0,
      extraGuestSurcharge: 0,
      taxesAndFees: 0,
      otherFees: item.otherFees,
    },
    seatSelectionPolicy: departing?.flightGroup.flights[0].seatSummary.selectionPolicy,
    isBundled,
    quotedFare: item.quotedFare,
    searchFlightsUrl: isBundled ?
        getCheckoutFlightV2SearchUrl(flightsWithJourneys.map(flight => journeys[flight.journeyId])) :
        getStandaloneFlightsV2SearchUrl(item, airports, journeys),
    viewType: item.viewType,
    isNDC,
    charterBundleRoute: item.charterBundleRoute,
  }

  return result
}

export const getFlightItemsView = createSelector(
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.flights.journeysById,
  (state: App.State) => state.geo.airports,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getCheckoutLoyaltyClassicRewards(state),
  (state: App.State) => getCheckoutLoyaltyClassicRewardsStatus(state),
  (flightItems, journeys, airports, postPurchaseMode, classicRewards, classicRewardStatus): App.WithDataStatus<Array<App.Checkout.FlightItemView>> => {
    const anyItemsFetching = flightItems.some(item => item.fetchingFare)
    const hasRewardFlights = flightItems.some(item => item.isClassicReward)
    const rewardAvailabilityBlocksTotals = hasRewardFlights && !!classicRewardStatus?.hasRewardFlights && !classicRewardStatus.available

    const flightViews = nonNullable(
      flightItems.map<{ hasRequiredData: boolean, view: App.Checkout.FlightItemView | undefined }>((item) => {
        let journeyKeys: Array<string>
        if (postPurchaseMode === 'flight-change-dates') {
          journeyKeys = item.flights.map(flight => getDateChangeJourneyV2IdKey(item.reservationId!, item.searchId!, flight?.journeyId))
        } else {
          journeyKeys = item.flights.map(flight => getJourneyV2IdKey(flight?.journeyId, item.searchId!, flight?.fareFamily?.id))
        }
        const flightJourneys = journeyKeys.map(key => journeys[key] as App.JourneyV2)
        const journeysByFlightJourneyId = arrayToObject(item.flights, flight => flight.journeyId, (_, index) => flightJourneys[index])

        const view = mapFlightV2ToView(
          item,
          airports,
          journeysByFlightJourneyId,
        )

        // Classic Rewards: use the cash co-pay as the flight price instead of the
        // full fare. All downstream selectors (totals, insurance, merchant fee, etc.)
        // automatically pick up the reward-adjusted price — no post-hoc deductions needed.
        const matchingReward = classicRewards?.find(r => r.itemTransactionKey === item.transactionKey)
        const adjustedView = view && matchingReward ? {
          ...view,
          totals: { ...view.totals, price: matchingReward.totalCash },
        } : view

        return {
          view: adjustedView,
          hasRequiredData: flightJourneys.filter(Boolean).length === item.flights.length,
        }
      }),
    )

    const hasAllRequiredData =
      flightViews.every(fv => fv.hasRequiredData) &&
      !anyItemsFetching &&
      !rewardAvailabilityBlocksTotals &&
      flightViews.length === flightItems.length

    return {
      hasRequiredData: hasAllRequiredData,
      data: nonNullable(flightViews.map(view => view.view)),
    }
  },
)

export const getFlightLuxPlusRegularPrice = createSelector(
  (state: App.State) => getFlightItemsView(state),
  (state: App.State) => getIsEnabledFlightsBundledLuxPlusPricing(state),
  (flightItemsWithStatus, isFlightsBundledLuxPlusPricingEnabled): App.WithDataStatus<number | undefined> => {
    const bundledItemRegularPrices = isFlightsBundledLuxPlusPricingEnabled ?
        flightItemsWithStatus.data.filter(itemView => itemView.isBundled).map(getFlightItemViewLuxPlusRegularPrice) :
        []
    const data = isFlightsBundledLuxPlusPricingEnabled ?
        bundledItemRegularPrices.reduce<number | undefined>((total, regularPrice) => {
          if (regularPrice === undefined) {
            return total
          }

          return (total ?? 0) + regularPrice
        }, undefined) :
      undefined

    return {
      hasRequiredData: flightItemsWithStatus.hasRequiredData,
      data,
    }
  },
)

export const getFlightBreakdownView = createSelector(
  (state: App.State) => getFlightItemsView(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.commissionData,
  (state: App.State) => getIsEnabledFlightsLuxPlusPricing(state),
  (state: App.State) => getCanRedeemLuxPlusBenefitsOnOrder(state),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getIsCurrentFlightDateChangeSelectionValid(state),
  (flightViewsWithStatus, postPurchaseMode, commissionData, isFlightsLuxPlusPricingEnabled, canRedeemLuxPlusBenefitsOnOrder, checkoutItems, isCurrentFlightDateChangeSelectionValid): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    if (postPurchaseMode === 'flight-change-dates' && !isCurrentFlightDateChangeSelectionValid) {
      return {
        hasRequiredData: true,
        data: [],
      }
    }

    const intl = getCurrentIntl()
    const canUseLuxPlusFlightPricing = canRedeemLuxPlusBenefitsOnOrder || checkoutItems.some(isLuxPlusSubscriptionItem)
    const flightBreakdownViews = flightViewsWithStatus.data.map<App.Checkout.PriceBreakdownView>(view => {
      let title: App.Checkout.PriceBreakdownTitle = customPriceBreakdownTitle(formatFareType(intl, view.fareType))
      if (postPurchaseMode === 'flight-change-dates') {
        title = knownPriceBreakdownTitle('newFlightAdditionalCost')
      }
      const supplementaryData = {
        validatingCarrierName: view.validatingCarrierName,
        validatingCarrierLogo: view.validatingCarrierLogo,
      }
      const useLuxPlusRegularPrice = !view.isBundled && isFlightsLuxPlusPricingEnabled && !canUseLuxPlusFlightPricing
      const flightItems = view.flights.map(flight => buildFlightItemBreakdownView(view, flight, supplementaryData, intl, useLuxPlusRegularPrice))
      const price = view.totals.price ?? 0
      const luxPlusRegularPrice = !view.isBundled && isFlightsLuxPlusPricingEnabled ?
          getFlightBreakdownLuxPlusRegularPrice(flightItems) :
        undefined
      const memberPrice = !view.isBundled && isFlightsLuxPlusPricingEnabled ? price : 0

      const breakdownView: App.Checkout.PriceBreakdownView = {
        title,
        breakdownItemType: 'flight',
        additionalInfoText: postPurchaseMode === 'flight-change-dates' ? [] : [
          ...(view.flights[0] ? [formatJourneySummaryTitle(view.flights, view.fareType as FlightsFareTypes, intl)] : []),
          formatFlightPassengersShorthand(view.passengers, intl),
        ],
        price: view.isBundled ? 0 : (luxPlusRegularPrice ?? price),
        memberPrice: view.isBundled ? 0 : memberPrice,
        items: postPurchaseMode === 'flight-change-dates'
          ? [buildDateChangeFlightItemBreakdownView(view, intl)]
          : flightItems,
        commission: commissionData?.itemsCommission?.[getAgentHubCommissionItemKey(view.item)],
      }

      return breakdownView
    })

    return {
      hasRequiredData: flightViewsWithStatus.hasRequiredData,
      data: flightBreakdownViews,
    }
  },
)

export const getAirlineChangeFeeBreakdownView = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.orders.flightDetails,
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.flights.journeysById,
  (state: App.State) => getFlightDateChangeSearchAirlineFeeJourneys(state),
  (state: App.State) => getShouldHideEstimatedAirlineChangeFee(state),
  (state: App.State) => getIsFlightDateChangeAirlineChangeFeeLoading(state),
  (state: App.State) => getIsCurrentFlightDateChangeSelectionValid(state),
  (order, allFlightDetails, flightItems, journeysById, searchAirlineFeeJourneys, hideEstimatedAirlineChangeFee, isAirlineChangeFeeLoading, isCurrentFlightDateChangeSelectionValid): App.Checkout.PriceBreakdownView => {
    const intl = getCurrentIntl()
    const selectedDateChangeJourneyState = isCurrentFlightDateChangeSelectionValid ?
        getSelectedDateChangeJourneys(flightItems, journeysById) :
        { hasSelectedJourneys: false, hasRequiredData: false, journeys: [] }
    const selectedDateChangeJourneys = selectedDateChangeJourneyState.journeys
    const airlineFeeJourneys = searchAirlineFeeJourneys.length > 0 ? searchAirlineFeeJourneys : selectedDateChangeJourneys
    const flightItemId = order?.flightItems[0]?.id
    const flightDetails = allFlightDetails[flightItemId ?? '']
    const travellers = flightDetails?.flight?.travellers || []
    const vendorFeeBreakdown = flightDetails?.flight?.flightChangePenalties?.fees.vendorFeeByCarrier?.[0]?.penaltyBreakdown || {}
    const hasAirlineFeeJourneys = airlineFeeJourneys.length > 0
    const price = hideEstimatedAirlineChangeFee || isAirlineChangeFeeLoading ? 0 : hasAirlineFeeJourneys ?
        sum(airlineFeeJourneys, journey => journey.price?.all?.airlineChangeFee ?? 0) :
      flightDetails?.flight?.flightChangePenalties?.fees.totalAmount || 0
    const items: Array<App.Checkout.AnyItemBreakdownView> = [
      {
        itemType: 'flights-date-change-fee',
        title: '',
        additionalInfo: [],
        additionalElements: hasAirlineFeeJourneys ?
            createAirlineChangeFeeTravellerElements(airlineFeeJourneys) :
            createTravellerElements(travellers, vendorFeeBreakdown),
      },
    ]

    return {
      title: knownPriceBreakdownTitle('airlineChangeFee'),
      priceLabel: hideEstimatedAirlineChangeFee ? intl.formatMessage({
        id: '8orKTIxrcimu0SW2hP7l',
        defaultMessage: 'Confirmed at date selection',
        description: 'Placeholder value shown while airline change fees are unavailable in the early flight date change flow',
      }) : undefined,
      price,
      alwaysShowItemPrice: true,
      additionalInfoText: [],
      detailInfoText: hideEstimatedAirlineChangeFee ? [
        intl.formatMessage({
          id: 'jwqkJcj_bEbujLfG0jYe',
          defaultMessage: 'Change fees vary by airline and fare type. We’ll show the exact amount once you select your new flights',
          description: 'Explanation shown in the flight date change summary before airline change fees can be confirmed',
        }),
      ] : undefined,
      items: hideEstimatedAirlineChangeFee || isAirlineChangeFeeLoading ? [] : items,
      memberPrice: 0,
    }
  },
)

export const getLuxuryEscapesChangeFeeBreakdownView = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.orders.flightDetails,
  (order, allFlightDetails): App.Checkout.PriceBreakdownView => {
    const flightItemId = order?.flightItems[0]?.id
    const flightDetails = allFlightDetails[flightItemId ?? '']
    const travellers = flightDetails?.flight?.travellers || []
    const luxuryEscapesChangeFee = flightDetails?.flight?.flightChangePenalties?.fees.handlingFee || 0
    const handlingFeeBreakdown = flightDetails?.flight?.flightChangePenalties?.fees.handlingFeeBreakdown || {}

    return {
      title: knownPriceBreakdownTitle('brandChangeFee'),
      price: luxuryEscapesChangeFee,
      alwaysShowItemPrice: true,
      additionalInfoText: [],
      items: [
        {
          itemType: 'flights-date-change-fee',
          title: '',
          additionalInfo: [],
          additionalElements: createTravellerElements(travellers, handlingFeeBreakdown),
        },
      ],
      memberPrice: 0,
    }
  },
)

export const getFlightsDateChangeBreakdownView = createSelector(
  (state: App.State) => getFlightItemsView(state),
  (state: App.State) => getAirlineChangeFeeBreakdownView(state),
  (state: App.State) => getLuxuryEscapesChangeFeeBreakdownView(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getIsFlightDateChangeAirlineChangeFeeLoading(state),
  (flightViewsWithStatus, airlineChangeFeeBreakdownView, luxuryEscapesChangeFeeBreakdownView, postPurchaseMode, isAirlineChangeFeeLoading): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    if (postPurchaseMode !== 'flight-change-dates') {
      return {
        hasRequiredData: flightViewsWithStatus.hasRequiredData,
        data: [],
      }
    }

    return {
      hasRequiredData: flightViewsWithStatus.hasRequiredData && !isAirlineChangeFeeLoading,
      data: [airlineChangeFeeBreakdownView, luxuryEscapesChangeFeeBreakdownView],
    }
  },
)

export const isStandaloneFlights = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getFlightItems(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.existingOrder,
  (allItems, flightItems, postPurchaseMode, order): boolean => {
    if (postPurchaseMode) {
      if (!order) return false
      return !!getAllOrderItems(order)?.every(
        item => isFlightOrderItem(item) || isInsuranceOrderItem(item) || isBookingProtectionOrderItem(item) || isLuxPlusSubscriptionOrderItem(item),
      )
    }

    // a standalone flight can still have insurance/booking protection and/or luxplus subscription items
    return flightItems.length > 0 && allItems.every(item => isFlightItem(item) || isInsuranceItem(item) || isBookingProtectionItem(item) || isLuxPlusSubscriptionItem(item) || isSubscriptionJoinItem(item))
  },
)

export const checkoutContainsFlightsWithEmbeddedInsurance = createSelector(
  (state: App.State) => getFlightItems(state),
  (flightItems) => hasCheckoutFlightItemsWithEmbeddedInsurance(flightItems),
)

/**
 * Returns aggregated flight addon rows (seat selections, baggage, meals, etc.)
 * computed directly from flight breakdown views and seat selections.
 */
export const getFlightAddonRows = createSelector(
  (state: App.State) => getFlightBreakdownView(state),
  (state: App.State) => getFlightSeatSelectionsByKey(state),
  (state: App.State) => getFlightBaggageSelectionsByKey(state),
  (flightBreakdownViewWithStatus, seatSelectionsByKey, baggageSelectionsByKey): Array<FlightServiceRowData> => {
    const flightItems = flightBreakdownViewWithStatus.data
      .flatMap(breakdown => breakdown.items)
      .filter((item): item is App.Checkout.FlightItemBreakdownView => item.itemType === 'flight')

    return aggregateFlightServiceRows(flightItems, seatSelectionsByKey, baggageSelectionsByKey)
  },
)

export const getFlightNumbersBySearchId = createSelector(
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.flights.seatsMap,
  (flightItems, seatMaps) => {
    const flightNumberMap: Record<string, Array<string>> = {}

    Object.values(seatMaps).forEach((values) => {
      if (values?.data?.seatMap && !values?.fetching) {
        values.data.seatMap.forEach((seatMap) => {
          const flightItem = flightItems.find((item) => {
            return item.flights.find((flight) => flight.journeyId === seatMap.fareId)
          })

          const searchId = flightItem?.searchId ?? ''

          const mappedSearchId = flightNumberMap[searchId] ?? []
          mappedSearchId.push(seatMap.paxSegmentRefID)
          flightNumberMap[searchId] = mappedSearchId
        })
      }
    })

    return flightNumberMap
  },
)

export const getIsInternationalFlight = createSelector(
  getFlightItemsView,
  (flightItemsView): boolean | undefined => {
    if (!flightItemsView.hasRequiredData) return undefined
    if (flightItemsView.data.length === 0) return undefined
    return flightItemsView.data.some(view =>
      view.flights.some(leg =>
        leg.journeyFlight.flights.some(segment =>
          segment.departingCountry !== segment.arrivalCountry,
        ),
      ),
    )
  },
)
